import React from 'react';
import { Link } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Star, ArrowRight } from 'lucide-react';
import { motion } from 'framer-motion';
import { useLanguage } from '@/contexts/LanguageContext';
import AnimateOnScroll, { StaggerContainer, StaggerItem } from '@/components/common/AnimateOnScroll';
import { useFeaturedTeachers } from '@/hooks/use-public-api';
import { specialtyLabel } from '@/lib/specialties';
import { useCurrency } from '@/contexts/CurrencyContext';
import type { FeaturedTeacher } from '@/types/api';

const stripHtml = (s?: string) => (s ? s.replace(/<[^>]*>/g, ' ').replace(/\s+/g, ' ').trim() : '');

const initialOf = (s: string) => (s ? s.trim().charAt(0).toUpperCase() : '?');

const SkeletonCard = () => (
  <div
    className="bg-card rounded-xl border border-border p-5 animate-pulse"
    aria-hidden="true"
  >
    <div className="flex items-start gap-4 mb-4">
      <div className="w-14 h-14 rounded-xl bg-muted shrink-0" />
      <div className="flex-1 space-y-2">
        <div className="h-4 w-2/3 bg-muted rounded" />
        <div className="h-3 w-1/3 bg-muted rounded" />
      </div>
      <div className="space-y-2">
        <div className="h-4 w-12 bg-muted rounded" />
        <div className="h-3 w-10 bg-muted rounded" />
      </div>
    </div>
    <div className="flex gap-1.5 mb-4">
      <div className="h-5 w-14 bg-muted rounded-md" />
      <div className="h-5 w-14 bg-muted rounded-md" />
    </div>
    <div className="h-4 w-1/2 bg-muted rounded mt-3" />
  </div>
);

const TeacherCard = ({ teacher, isRTL }: { teacher: FeaturedTeacher; isRTL: boolean }) => {
  const rating = Number(teacher.average_rating ?? 0).toFixed(1);
  const sessions = teacher.total_sessions ?? 0;
  const specialties = (teacher.specialties || []).slice(0, 3);
  const displayName = isRTL && teacher.full_name_ar ? teacher.full_name_ar : teacher.full_name;
  const { formatPrice } = useCurrency();
  return (
    <Link to={`/teachers/${encodeURIComponent(teacher.slug || teacher.name)}`}>
      <motion.div
        whileHover={{ y: -4 }}
        transition={{ duration: 0.2 }}
        className="bg-card rounded-xl border border-border p-5 hover:shadow-soft-lg hover:border-primary/20 transition-all duration-300 group"
      >
        <div className="flex items-start gap-4 mb-4">
          <div className="w-14 h-14 rounded-xl bg-primary/10 flex items-center justify-center shrink-0 overflow-hidden group-hover:bg-primary/15 transition-colors">
            {teacher.profile_image ? (
              <img
                src={teacher.profile_image}
                alt={displayName}
                loading="lazy"
                decoding="async"
                className="w-full h-full object-cover"
              />
            ) : (
              <span className="text-primary font-arabic text-xl font-bold">
                {initialOf(displayName)}
              </span>
            )}
          </div>
          <div className="flex-1 min-w-0">
            <h3 className="font-semibold text-foreground truncate">{displayName}</h3>
            <div
              className="flex items-center gap-1.5 mt-1"
              aria-label={isRTL ? `${rating} من 5 نجوم` : `${rating} out of 5 stars`}
            >
              <Star className="h-3.5 w-3.5 text-amber-500 fill-amber-500" aria-hidden="true" />
              <span aria-hidden="true" className="text-sm font-medium text-foreground">{rating}</span>
            </div>
          </div>
          <div className="text-end shrink-0">
            <p className="text-lg font-bold text-foreground">
              {formatPrice(teacher.hourly_rate)}
            </p>
            <p className="text-[10px] text-muted-foreground uppercase">
              {isRTL ? '/ساعة' : '/hour'}
            </p>
          </div>
        </div>

        {specialties.length > 0 && (
          <div className="flex flex-wrap gap-1.5 mb-4">
            {specialties.map((s) => (
              <span
                key={s}
                className="px-2.5 py-1 bg-primary/8 text-primary text-xs font-medium rounded-md"
              >
                {specialtyLabel(s, isRTL)}
              </span>
            ))}
          </div>
        )}

        {teacher.bio && (
          <p className="text-xs text-muted-foreground line-clamp-2 mb-3">
            {stripHtml(teacher.bio)}
          </p>
        )}

        <div className="flex items-center justify-between pt-3 border-t border-border">
          <p className="text-xs text-muted-foreground">
            <span className="font-medium text-foreground">{sessions}</span>{' '}
            {isRTL ? 'جلسة' : 'sessions'}
          </p>
          <span className="text-xs font-medium text-primary group-hover:underline">
            {isRTL ? 'احجز جلسة تجريبية' : 'Book Trial'} →
          </span>
        </div>
      </motion.div>
    </Link>
  );
};

const FeaturedTeachers = () => {
  const { isRTL } = useLanguage();
  const { data, isLoading, isError } = useFeaturedTeachers(6);

  const teachers: FeaturedTeacher[] = Array.isArray(data) ? data : [];

  return (
    <section className="section bg-muted/30" dir={isRTL ? 'rtl' : 'ltr'}>
      <div className="container">
        <AnimateOnScroll className="flex items-end justify-between mb-10">
          <div>
            <p className="text-sm font-semibold text-amber-700 dark:text-amber-400 uppercase tracking-wider mb-2">
              {isRTL ? 'معلمون مميزون' : 'Featured Teachers'}
            </p>
            <h2 className="text-3xl md:text-4xl font-bold text-foreground">
              {isRTL ? 'تعلّم من الأفضل' : 'Learn From the Best'}
            </h2>
          </div>
          <Button asChild variant="ghost" className="hidden sm:flex items-center gap-1.5 text-primary">
            <Link to="/teachers">
              {isRTL ? 'عرض الكل' : 'View All'}
              <ArrowRight className="h-4 w-4 rtl-mirror" />
            </Link>
          </Button>
        </AnimateOnScroll>

        {isLoading && (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5">
            <SkeletonCard />
            <SkeletonCard />
            <SkeletonCard />
          </div>
        )}

        {!isLoading && isError && (
          <div className="text-center py-12 text-muted-foreground">
            <p>{isRTL ? 'تعذّر تحميل المعلمين الآن.' : "We couldn't load teachers right now."}</p>
            <Button asChild variant="outline" className="mt-4 rounded-xl">
              <Link to="/teachers">{isRTL ? 'تصفح جميع المعلمين' : 'Browse all teachers'}</Link>
            </Button>
          </div>
        )}

        {!isLoading && !isError && teachers.length === 0 && (
          <div className="text-center py-12 text-muted-foreground">
            <p>{isRTL ? 'لا يوجد معلمون مميزون حاليًا.' : 'No featured teachers available yet.'}</p>
            <Button asChild variant="outline" className="mt-4 rounded-xl">
              <Link to="/teachers">{isRTL ? 'تصفح جميع المعلمين' : 'Browse all teachers'}</Link>
            </Button>
          </div>
        )}

        {!isLoading && !isError && teachers.length > 0 && (
          <StaggerContainer className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5">
            {teachers.map((teacher) => (
              <StaggerItem key={teacher.name}>
                <TeacherCard teacher={teacher} isRTL={isRTL} />
              </StaggerItem>
            ))}
          </StaggerContainer>
        )}

        <div className="sm:hidden mt-6 text-center">
          <Button asChild variant="outline" className="rounded-xl">
            <Link to="/teachers" className="flex items-center gap-1.5">
              {isRTL ? 'عرض جميع المعلمين' : 'View All Teachers'}
              <ArrowRight className="h-4 w-4 rtl-mirror" />
            </Link>
          </Button>
        </div>
      </div>
    </section>
  );
};

export default FeaturedTeachers;
